/**
 * FirmwareConsole — the platform-admin OTA control plane (a section of the
 * platform console). Upload a signed firmware release (self-hosted in Postgres),
 * roll it out to all or selected claimed gateways, watch per-device running
 * version + OTA acknowledgement, and roll back by re-rolling a prior release.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, Rocket, Trash2, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Badge } from "@/lib/ui/badge";
import { useToast } from "@/lib/ui/toast";
import { backendErrorMessage } from "@/lib/api";
import { firmware } from "@/lib/api/firmware";

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const OTA_STATE_TONE: Record<string, string> = {
  applied: "bg-success/15 text-success",
  downloading: "bg-info/15 text-info",
  offered: "bg-surface text-on-surface-muted",
  failed: "bg-danger/15 text-danger",
};

export function FirmwareConsole() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rolloutVersion, setRolloutVersion] = useState<string>("");
  const [confirmRollout, setConfirmRollout] = useState<string | null>(null); // release id awaiting all-fleet confirm

  const releasesQ = useQuery({
    queryKey: ["firmware", "releases"],
    queryFn: () => firmware.listReleases(),
  });
  const devicesQ = useQuery({
    queryKey: ["firmware", "devices"],
    queryFn: () => firmware.listDevices(),
    refetchInterval: 15_000, // versions + acks trickle in
  });

  const releases = releasesQ.data?.releases ?? [];
  const devices = devicesQ.data?.devices ?? [];

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["firmware", "releases"] });
    void qc.invalidateQueries({ queryKey: ["firmware", "devices"] });
  };

  const rolloutM = useMutation({
    mutationFn: (v: { releaseId: string; body: { thing_ids?: string[]; all?: boolean } }) =>
      firmware.rollout(v.releaseId, v.body),
    onSuccess: (r) => {
      toast.push("ok", `Rollout ${r.version}: offered to ${r.offered}/${r.targeted} device(s).`);
      setSelected(new Set());
      setConfirmRollout(null);
      refresh();
    },
    onError: (e) => toast.push("error", backendErrorMessage(e)),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => firmware.remove(id),
    onSuccess: () => {
      toast.push("ok", "Release deleted.");
      refresh();
    },
    onError: (e) => toast.push("error", backendErrorMessage(e)),
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const selectedRelease = useMemo(
    () => releases.find((r) => r.version === rolloutVersion),
    [releases, rolloutVersion],
  );

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-on-surface">Firmware / OTA</h2>
        <p className="text-xs text-on-surface-muted">
          Upload a signed release, roll it out to all or selected gateways, and watch each device
          confirm. Rollback = roll a prior release out again.
        </p>
      </div>

      <UploadForm onDone={refresh} />

      {/* releases */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-surface-elevated text-[11px] uppercase tracking-wide text-on-surface-muted">
            <tr>
              <th className="px-3 py-2 text-left">Version</th>
              <th className="px-3 py-2 text-left">Channel</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2 text-right">Running</th>
              <th className="px-3 py-2 text-left">Signed</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {releasesQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-on-surface-muted">
                  <Loader2 className="mx-auto size-4 animate-spin" />
                </td>
              </tr>
            ) : releases.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-xs text-on-surface-muted">
                  No releases yet — upload a firmware build to begin.
                </td>
              </tr>
            ) : (
              releases.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-on-surface">{r.version}</td>
                  <td className="px-3 py-2 text-on-surface-muted">{r.channel}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-on-surface-muted">
                    {fmtBytes(r.size_bytes)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.devices_running}</td>
                  <td className="px-3 py-2">
                    {r.has_signature ? (
                      <Badge className="bg-success/15 text-success">
                        <ShieldCheck className="mr-1 size-3" /> Ed25519
                      </Badge>
                    ) : (
                      <Badge className="bg-warning/15 text-warning">unsigned</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant={confirmRollout === r.id ? "primary" : "outline"}
                        disabled={rolloutM.isPending}
                        onClick={() =>
                          confirmRollout === r.id
                            ? rolloutM.mutate({ releaseId: r.id, body: { all: true } })
                            : setConfirmRollout(r.id)
                        }
                        title="Roll out to every claimed gateway"
                      >
                        {r.devices_running > 0 ? (
                          <RotateCcw className="mr-1 size-3.5" />
                        ) : (
                          <Rocket className="mr-1 size-3.5" />
                        )}
                        {confirmRollout === r.id ? "Confirm — all gateways" : "Roll out to all"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:bg-danger/10"
                        disabled={deleteM.isPending}
                        onClick={() => deleteM.mutate(r.id)}
                        aria-label={`Delete ${r.version}`}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* selected-device rollout bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2">
          <span className="text-sm text-on-surface">{selected.size} device(s) selected →</span>
          <select
            aria-label="Release to roll out"
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface"
            value={rolloutVersion}
            onChange={(e) => setRolloutVersion(e.target.value)}
          >
            <option value="">Select release…</option>
            {releases.map((r) => (
              <option key={r.id} value={r.version}>
                {r.version} ({r.channel})
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={!selectedRelease || rolloutM.isPending}
            onClick={() =>
              selectedRelease &&
              rolloutM.mutate({
                releaseId: selectedRelease.id,
                body: { thing_ids: Array.from(selected) },
              })
            }
          >
            <Rocket className="mr-1 size-3.5" /> Roll out to selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* devices */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-surface-elevated text-[11px] uppercase tracking-wide text-on-surface-muted">
            <tr>
              <th className="w-8 px-3 py-2"></th>
              <th className="px-3 py-2 text-left">Gateway</th>
              <th className="px-3 py-2 text-left">Running</th>
              <th className="px-3 py-2 text-left">Last OTA</th>
              <th className="px-3 py-2 text-left">When</th>
            </tr>
          </thead>
          <tbody>
            {devicesQ.isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-on-surface-muted">
                  <Loader2 className="mx-auto size-4 animate-spin" />
                </td>
              </tr>
            ) : devices.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-xs text-on-surface-muted">
                  No claimed gateways.
                </td>
              </tr>
            ) : (
              devices.map((d) => (
                <tr key={d.thing_id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(d.thing_id)}
                      onChange={() => toggle(d.thing_id)}
                      aria-label={`Select ${d.name}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-on-surface">{d.name}</td>
                  <td className="px-3 py-2 tabular-nums text-on-surface">
                    {d.running_version ?? <span className="text-on-surface-muted">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {d.last_ota_state ? (
                      <Badge className={OTA_STATE_TONE[d.last_ota_state] ?? "bg-surface"}>
                        {d.last_ota_state}
                        {d.last_ota_version ? ` · ${d.last_ota_version}` : ""}
                      </Badge>
                    ) : (
                      <span className="text-on-surface-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-on-surface-muted">
                    {d.last_ota_at ? new Date(d.last_ota_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UploadForm({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState("stable");
  const [notes, setNotes] = useState("");
  const [minKernel, setMinKernel] = useState("");
  const [sig, setSig] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const uploadM = useMutation({
    mutationFn: () =>
      firmware.upload({
        version: version.trim(),
        channel,
        notes: notes.trim() || undefined,
        min_kernel: minKernel.trim() || undefined,
        ed25519_sig: sig.trim() || undefined,
        file: file as File,
      }),
    onSuccess: () => {
      toast.push("ok", `Release ${version} uploaded.`);
      setOpen(false);
      setVersion("");
      setNotes("");
      setMinKernel("");
      setSig("");
      setFile(null);
      onDone();
    },
    onError: (e) => toast.push("error", backendErrorMessage(e)),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="self-start" onClick={() => setOpen(true)}>
        <Upload className="mr-1.5 size-3.5" /> Upload release
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-elevated p-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Version *
          <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="4.0.1" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Channel
          <select
            aria-label="Channel"
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="stable">stable</option>
            <option value="beta">beta</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Min kernel (optional)
          <Input value={minKernel} onChange={(e) => setMinKernel(e.target.value)} placeholder="5.10" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Ed25519 signature (base64)
          <Input value={sig} onChange={(e) => setSig(e.target.value)} placeholder="detached sig" />
        </label>
        <label className="col-span-2 flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Notes
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="changelog" />
        </label>
        <label className="col-span-2 flex flex-col gap-1 text-[11px] text-on-surface-muted">
          Artifact *
          <input
            type="file"
            className="text-xs text-on-surface"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!version.trim() || !file || uploadM.isPending}
          onClick={() => uploadM.mutate()}
        >
          {uploadM.isPending ? (
            <Loader2 className="mr-1 size-3.5 animate-spin" />
          ) : (
            <Upload className="mr-1 size-3.5" />
          )}
          Upload
        </Button>
      </div>
    </div>
  );
}
